/* Copyright (c) 2025-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
    ViewChild
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { SpatialNavigationContainerDirective } from '../../directives/spatial-navigation-container.directive';
import { FocusManager } from '../../services/focus-manager';
import {
    NavigationEvent,
    SpatialNavigationDirection
} from '../../services/spatial-navigation/spatial-navigation-polyfill';
import { SpatialNavigationService } from '../../services/spatial-navigation/spatial-navigation.service';
import { HigNavItem, HigNavItemClick } from '../../types/hig-shared.types';

@Component({
    selector: 'nv-hig-tabs',
    templateUrl: './nv-hig-tabs.component.html',
    styleUrl: './nv-hig-tabs.component.scss',
    imports: [MatButtonModule, MatIconModule, TranslateModule, SpatialNavigationContainerDirective]
})
export class NvHigTabsComponent<T> implements AfterViewInit, OnDestroy {
    /**
     * Whether to distribute tabs evenly across the container width (true) or let tabs fit their content (false).
     * When true, each tab width = container width / number of tabs
     */
    @Input() fixedWidth = false;

    /** The currently selected tab item */
    @Input() currentSelectedItem: HigNavItem<T>;

    /** Array of all tab items to display */
    @Input() tabItems: HigNavItem<T>[];

    /** Emits when a tab is clicked with event details and tab type */
    @Output() itemClicked = new EventEmitter<HigNavItemClick<T>>();

    @ViewChild('tabContainer') private tabContainer: ElementRef<HTMLElement>;

    private logger: Logger;
    private destroy$: Subject<void> = new Subject();

    constructor(
        loggingService: LoggingService,
        private spatialNavigationService: SpatialNavigationService,
        private ngZone: NgZone,
        private focusManager: FocusManager
    ) {
        this.logger = loggingService.getLogger('NvHigTabsComponent');
    }

    ngAfterViewInit(): void {
        if (this.spatialNavigationService.enabled) {
            /**
             * Note: If need to update the binding properties with the subscriptions that are called
             * outside the Angular zone, should manually trigger the change detection to update the view in same tick.
             */
            this.ngZone.runOutsideAngular(() => {
                // Subscribe to navbeforefocus to intercept the focus before it is moved inside the tabs
                fromEvent<NavigationEvent>(this.tabContainer.nativeElement, 'navbeforefocus')
                    .pipe(
                        filter(e => e.target === this.tabContainer.nativeElement),
                        takeUntil(this.destroy$)
                    )
                    .subscribe(this.onNavigateToInside.bind(this));
            });
        }
        this.scrollSelectedTabIntoView();
    }

    /**
     * Handles spatial navigation when focus moves into the tab container.
     * Prevents default behavior and redirects focus to the selected tab.
     * @param event The navigation event containing direction information
     */
    private onNavigateToInside(event: NavigationEvent): void {
        event.preventDefault();
        this.moveFocusOnSelectedItem(event.detail.dir);
    }

    /**
     * Programmatically moves focus to the currently selected tab.
     * Used when navigating into the tab container via spatial navigation.
     *
     * @param dir The direction of focus movement for spatial navigation.
     *            If provided, uses navigateTo() method.
     *            If not provided, uses focusViaLastOrigin() method.
     */
    moveFocusOnSelectedItem(dir?: SpatialNavigationDirection): void {
        // Use requestAnimationFrame for better performance
        requestAnimationFrame(() => {
            const currentSelectedButton = this.tabContainer?.nativeElement.querySelector(
                '.selected-tab'
            ) as HTMLElement;

            if (!currentSelectedButton || currentSelectedButton === this.focusManager.currentFocusedElement) {
                return;
            }

            if (dir) {
                this.focusManager.navigateTo(currentSelectedButton, dir);
            } else {
                this.focusManager.focusViaLastOrigin(currentSelectedButton);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Handles tab button click events.
     * Emits a structured event containing both the mouse event and the tab type.
     *
     * @param event The original mouse click event
     * @param itemType The type/identifier of the clicked tab
     */
    onItemClick(event: MouseEvent, itemType: T): void {
        this.itemClicked.emit({
            event,
            itemType
        });
    }

    /**
     * Scrolls the currently selected tab into view with smooth animation.
     * Ensures the selected tab is visible within the scrollable container.
     * Called on initial load and can be used when selection changes.
     */
    private scrollSelectedTabIntoView(): void {
        const selectedTab = this.tabContainer?.nativeElement.querySelector('.selected-tab') as HTMLElement;
        if (selectedTab) {
            // Use requestAnimationFrame for smoother scrolling
            requestAnimationFrame(() => {
                selectedTab.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            });
        }
    }
}
